import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Today as TodayIcon,
  CalendarMonth as CalendarIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { timeTrackingApi } from "../../../services/timetracking-api";
import {
  TimeEntry,
  TimesheetReport,
  getStatusColor,
  getStatusText,
  formatDuration,
  formatBreakDuration,
} from "../../../types/timetracking";
import {
  formatTime,
  formatDate,
  formatDateOnly,
} from "../../../services/dateTimeService";
import { useAuth } from "../../../contexts/AuthContext";

const MyTimesheet: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [timesheetReport, setTimesheetReport] =
    useState<TimesheetReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [searchTerm, setSearchTerm] = useState("");

  // Get employee ID from auth context
  const employeeId = user?.id || 0;

  // Merge multiple sessions of the same day into one entry
  const mergeDailySessions = (entries: TimeEntry[]): TimeEntry[] => {
    const groupedByDate = entries.reduce(
      (acc, entry) => {
        const date = formatDate(entry.timeIn);
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(entry);
        return acc;
      },
      {} as Record<string, TimeEntry[]>,
    );

    return Object.values(groupedByDate).map((dayEntries) => {
      if (dayEntries.length === 1) {
        return dayEntries[0];
      }

      // Sort by timeIn to get earliest and latest
      const sorted = [...dayEntries].sort(
        (a, b) => new Date(a.timeIn).getTime() - new Date(b.timeIn).getTime(),
      );

      // Calculate total hours
      const totalHours = sorted.reduce(
        (sum, entry) => sum + entry.totalHours,
        0,
      );

      // Calculate total break minutes
      const totalBreakMinutes = sorted.reduce(
        (sum, entry) => sum + (entry.totalBreakMinutes || 0),
        0,
      );

      // Check if workday is completed (8h work + 45min lunch = 8.75h, or 4h with half-day leave)
      const hasHalfDayLeave = sorted.some((e) => e.hasHalfDayLeave);
      const isWorkdayCompleted =
        totalHours >= 8.75 || (hasHalfDayLeave && totalHours >= 4);

      // Merge admin notes (filter out empty ones)
      const adminNotes = sorted
        .map((e) => e.adminEditNote)
        .filter((note) => note && note.trim())
        .join("; ");

      // Merge user notes (filter out empty ones)
      const userNotes = sorted
        .map((e) => e.notes)
        .filter((note) => note && note.trim())
        .join("; ");

      // Use earliest timeIn and latest timeOut
      const mergedEntry: TimeEntry = {
        ...sorted[0],
        timeOut: sorted[sorted.length - 1].timeOut,
        totalHours,
        totalBreakMinutes:
          totalBreakMinutes > 0 ? totalBreakMinutes : undefined,
        isWorkdayCompleted,
        hasHalfDayLeave,
        status: sorted[sorted.length - 1].status, // Use latest status
        adminEditNote: adminNotes || undefined,
        notes: userNotes || undefined,
      };

      return mergedEntry;
    });
  };

  const loadTimesheet = async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    try {
      setError(null);
      const report = await timeTrackingApi.getPersonalTimesheetReport(
        employeeId,
        formatDateOnly(startDate),
        formatDateOnly(endDate),
      );
      setTimesheetReport(report);
      // Merge sessions for display
      setTimeEntries(mergeDailySessions(report.timeEntries));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("error.failedToLoadTimesheet"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleQuickFilter = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(start);
    setEndDate(end);
  };

  const filteredEntries = timeEntries.filter(
    (entry) =>
      entry.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatDate(entry.timeIn).includes(searchTerm),
  );

  const handleExportCsv = async () => {
    if (!startDate || !endDate) return;

    try {
      // Get browser timezone
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const csvData = await timeTrackingApi.exportTimesheetDataAsCsv(
        formatDateOnly(startDate),
        formatDateOnly(endDate),
        employeeId, // Pass employeeId for personal export
        browserTimezone, // timezone for local conversion
      );

      // Create and download CSV file
      const blob = new Blob([csvData], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `my_timesheet_${formatDateOnly(startDate)}_to_${formatDateOnly(endDate)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to export data");
    }
  };

  useEffect(() => {
    loadTimesheet();
  }, [startDate, endDate]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ pt: 0, pr: 2, pb: 2, pl: 2 }}>
        {/* Filters and Controls */}
        <Card elevation={1} sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              {/* Date Range Filters */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label={t("timetracking.labels.startDate")}
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label={t("timetracking.labels.endDate")}
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                    },
                  }}
                />
              </Grid>

              {/* Search */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("common.search")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                    ),
                  }}
                />
              </Grid>

              {/* Quick Filters */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    size="small"
                    startIcon={<TodayIcon />}
                    onClick={() => handleQuickFilter(7)}
                    variant="outlined"
                  >
                    {t("timetracking.filters.7days")}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<CalendarIcon />}
                    onClick={() => handleQuickFilter(30)}
                    variant="outlined"
                  >
                    {t("timetracking.filters.30days")}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {timesheetReport && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={2}>
                <CardContent>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("timetracking.totalHours")}
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {formatDuration(timesheetReport.totalHours)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={2}>
                <CardContent>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("timetracking.labels.workingDays")}
                  </Typography>
                  <Typography variant="h4" color="secondary.main">
                    {timesheetReport.totalDays}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={2}>
                <CardContent>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("timetracking.averageHours")}
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {formatDuration(timesheetReport.averageHoursPerDay)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={2}>
                <CardContent>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("timetracking.timesheet.totalSessions")}
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {timesheetReport.timeEntries.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Error Display */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={loadTimesheet}>
                {t("common.retry")}
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Time Entries Table */}
        <Card elevation={2}>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" component="div">
                {t("timetracking.timesheet.details")} ({filteredEntries.length}{" "}
                {t("timetracking.timesheet.entriesLabel")})
              </Typography>
              <Button
                startIcon={<DownloadIcon />}
                variant="outlined"
                size="small"
                onClick={handleExportCsv}
                disabled={loading || filteredEntries.length === 0}
              >
                {t("timetracking.actions.exportData")}
              </Button>
            </Box>

            {filteredEntries.length === 0 ? (
              <Alert severity="info">
                {t("timetracking.timesheet.noData")}
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size={isMobile ? "small" : "medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("timetracking.labels.workDate")}</TableCell>
                      <TableCell>{t("timetracking.labels.timeIn")}</TableCell>
                      <TableCell>{t("timetracking.labels.timeOut")}</TableCell>
                      <TableCell>{t("timetracking.labels.duration")}</TableCell>
                      {!isMobile && (
                        <TableCell>
                          {t("timetracking.labels.breakTime")}
                        </TableCell>
                      )}
                      <TableCell>{t("common.status")}</TableCell>
                      {!isMobile && (
                        <TableCell>{t("timetracking.labels.notes")}</TableCell>
                      )}
                      {!isMobile && (
                        <TableCell>
                          {t("timetracking.labels.adminNote")}
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEntries.map((entry) => (
                      <TableRow key={entry.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {formatDate(entry.timeIn)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatTime(entry.timeIn)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {entry.timeOut ? formatTime(entry.timeOut) : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {formatDuration(entry.totalHours)}
                          </Typography>
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <Typography
                              variant="body2"
                              color={
                                entry.totalBreakMinutes
                                  ? "info.main"
                                  : "text.secondary"
                              }
                            >
                              {entry.totalBreakMinutes
                                ? formatBreakDuration(entry.totalBreakMinutes)
                                : "-"}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Chip
                            label={t(
                              getStatusText(
                                entry.status,
                                entry.isWorkdayCompleted,
                                entry.isOnBreak,
                              ),
                            )}
                            color={
                              getStatusColor(
                                entry.status,
                                entry.isWorkdayCompleted,
                                entry.isOnBreak,
                              ) as any
                            }
                            size="small"
                          />
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {entry.notes || "-"}
                            </Typography>
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell>
                            {entry.adminEditNote ? (
                              <Typography
                                variant="body2"
                                color="warning.main"
                                sx={{ fontStyle: "italic" }}
                              >
                                {entry.adminEditNote}
                              </Typography>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default MyTimesheet;
